// umlaut-check: skip — Datenschluessel ("menge_fakturiert", "bedarf_bereinigt"
// usw.) sind technische Bezeichner aus cosmos_bedarfe.

import React from "react";
import {Bar, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import ChartCard from "../Statistics/ChartCard.jsx";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import {formatKw, formatMenge} from "./format.js";

const FARBE_FAKTURIERT = "#495057";
const FARBE_OHNE_BESTAND = "#f8d7da";
const FARBE_BEREINIGT = "#0d6efd";
const FARBE_B2B = "#fd7e14";
const FARBE_B2C = "#20c997";

function CustomTooltip({active, payload, label}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }
    const zeile = payload[0]?.payload || {};

    return (
        <div className="bg-white border rounded-1 shadow-sm p-2 small" style={{maxWidth: 260}}>
            <div className="fw-semibold mb-1">{formatKw(label)}</div>
            <div>Fakturiert: {formatMenge(zeile.menge_fakturiert)}</div>
            <div style={{color: FARBE_BEREINIGT}}>Bereinigt: {formatMenge(zeile.bedarf_bereinigt)}</div>
            {zeile.menge_gratis > 0 && <div className="text-muted">davon Gratis: {formatMenge(zeile.menge_gratis)}</div>}
            {zeile.menge_set_bestandteil > 0 && <div className="text-muted">davon Set-Bestandteil: {formatMenge(zeile.menge_set_bestandteil)}</div>}
            <div style={{color: FARBE_B2B}}>B2B-Shop bestellt: {formatMenge(zeile.menge_shop_b2b)}</div>
            <div style={{color: FARBE_B2C}}>B2C-Shop bestellt: {formatMenge(zeile.menge_shop_b2c)}</div>
            {zeile.menge_unerfuellt > 0 && <div className="text-muted">unerfüllt (Majesty): {formatMenge(zeile.menge_unerfuellt)}</div>}
            {zeile.tage_ohne_bestand > 0 && <div className="text-danger">{zeile.tage_ohne_bestand} Tage ohne Bestand</div>}
        </div>
    );
}

/**
 * Verbrauch der letzten zwei Jahre je Woche (PLAN.md Abschnitt 5):
 * fakturierte Menge als Balken (rosa eingefärbt in Wochen ohne Bestand),
 * bereinigter Bedarf als Linie, B2B-/B2C-Shop-Bestellmengen zum Vergleich -
 * ausdrücklich ohne gemeinsamen Schlüssel mit der Faktura (PLAN.md 4a.1b).
 */
export default function ArtikelVerbrauchChart({bedarfe, hinweise, hoehe = 280}) {
    const zeilen = (bedarfe || []).slice(-104);
    const hatDaten = zeilen.some((z) => z.menge_fakturiert > 0 || z.menge_shop_b2b > 0 || z.menge_shop_b2c > 0);

    return (
        <ChartCard
            title="Verbrauch, zwei Jahre"
            help="Fakturierte Menge je Kalenderwoche (Balken, rosa in Wochen ohne Bestand), bereinigter Bedarf (Linie) und die Bestellmengen aus B2B- und B2C-Shop zum Vergleich – die Shop-Mengen fließen NICHT in den bereinigten Bedarf ein, es gibt keinen gemeinsamen Schlüssel mit der Faktura."
            loading={false}
            empty={!hatDaten}
            height={hoehe}
        >
            {(hinweise || []).length > 0 && (
                <div className="d-flex flex-wrap gap-1 mb-2">
                    {hinweise.map((h, i) => (
                        <span key={i} className="badge text-bg-light border small">
                            {h}
                        </span>
                    ))}
                </div>
            )}
            <ResponsiveContainer width="100%" height={hoehe}>
                <ComposedChart data={zeilen} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                    <XAxis dataKey="kw" tickFormatter={formatKw} minTickGap={40} tick={{fontSize: 11}} />
                    <YAxis tickFormatter={formatMenge} tick={{fontSize: 11}} width={60} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="menge_fakturiert" name="Fakturiert" barSize={6} isAnimationActive={false}>
                        {zeilen.map((z, i) => (
                            <Cell key={i} fill={z.tage_ohne_bestand > 0 ? FARBE_OHNE_BESTAND : FARBE_FAKTURIERT} />
                        ))}
                    </Bar>
                    <Line type="monotone" dataKey="bedarf_bereinigt" name="Bereinigt" stroke={FARBE_BEREINIGT} strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="menge_shop_b2b" name="B2B-Shop bestellt" stroke={FARBE_B2B} strokeWidth={1.5} strokeDasharray="4 2" dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="menge_shop_b2c" name="B2C-Shop bestellt" stroke={FARBE_B2C} strokeWidth={1.5} strokeDasharray="4 2" dot={false} isAnimationActive={false} />
                </ComposedChart>
            </ResponsiveContainer>
            <div className="d-flex flex-wrap gap-3 mt-2 small">
                <HeaderWithHelp help="Fakturierte Menge je Woche, rosa in Wochen ohne Bestand.">
                    <span className="d-inline-flex align-items-center gap-1">
                        <span style={{display: "inline-block", width: 10, height: 10, backgroundColor: FARBE_FAKTURIERT}} aria-hidden="true" />
                        Fakturiert
                    </span>
                </HeaderWithHelp>
                <HeaderWithHelp help="Fakturiert + unerfüllte Nachfrage + Tage ohne Bestand × Normalbedarf.">
                    <span className="d-inline-flex align-items-center gap-1">
                        <span style={{display: "inline-block", width: 14, height: 3, backgroundColor: FARBE_BEREINIGT}} aria-hidden="true" />
                        Bereinigt
                    </span>
                </HeaderWithHelp>
                <HeaderWithHelp help="Bestellte Menge im B2B-Shop – reiner Vergleichsmaßstab, kein gemeinsamer Schlüssel mit der Faktura.">
                    <span className="d-inline-flex align-items-center gap-1">
                        <span style={{display: "inline-block", width: 14, height: 3, backgroundColor: FARBE_B2B}} aria-hidden="true" />
                        B2B-Shop
                    </span>
                </HeaderWithHelp>
                <HeaderWithHelp help="Bestellte Menge im B2C-Shop – reiner Vergleichsmaßstab, kein gemeinsamer Schlüssel mit der Faktura.">
                    <span className="d-inline-flex align-items-center gap-1">
                        <span style={{display: "inline-block", width: 14, height: 3, backgroundColor: FARBE_B2C}} aria-hidden="true" />
                        B2C-Shop
                    </span>
                </HeaderWithHelp>
            </div>
        </ChartCard>
    );
}
